import { useMemo, useState } from "react";
import * as XLSX from "xlsx";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { importInventory } from "../api/inventory";

// Excel column labels that map directly to variant_dimensions.label in DB.
const DIMENSION_COLUMN_LABELS = [
  "Kağıt Cinsi",
  "Gramaj",
  "Bobin Eni",
  "Mikron Kalınlığı",
  "Renk",
  "Pantone Kodu",
  "Koli Eni",
  "Koli Boyu",
  "Koli Yüksekliği",
];

// Fixed columns (non-variant metadata).
const FIXED_COLUMN_LABELS = {
  material_code: "Malzeme Kodu",
  material_name: "Malzeme Adı",
  customer_name: "Müşteri",
  order_no: "Sipariş No",
  line_no: "Sipariş Kalemi",
  supplier_name: "Tedarikçi",
  inbound_doc_no: "İrsaliye No",
  received_by_name: "Teslim Alan",
  box_code: "Koli Kodu",
  qty: "Miktar",
  uom: "Birim",
  qty_kg: "2.Miktar",
  uom2: "2.Birim",
  lot_no: "Lot No",
  unit_cost: "Birim Fiyat",
  price_unit: "Fiyatın Birimi",
  currency: "Döviz",
};

// Başlıksız içe aktarma engellenmesin diye zorunlu sayılan sütunlar.
// Bunlar yoksa hiçbir satır ayrıştırılamaz.
const REQUIRED_FIXED_COLUMNS = ["material_code", "qty"];

function normalizeHeaderKey(x) {
  return String(x || "")
    .trim()
    .toLocaleLowerCase("tr")
    .replace(/\s+/g, " ")
    // "İRSALİYE NO" ve "IRSALIYE NO" tr-locale küçültmede farklı sonuç verir (i/ı).
    // Noktalı/noktasız i ayrımını sil ki başlık eşleşmesi ikisini de yakalasın.
    .replace(/[ıi̇]/g, "i");
}

function toNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(String(value).replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

function toInt(value) {
  const n = toNumber(value);
  if (n === null) return null;
  return Number.isInteger(n) ? n : Math.trunc(n);
}

function toStr(value) {
  if (value === null || value === undefined) return "";
  return String(value).trim();
}

/**
 * Başlık satırını bulur. Sabit "ilk satır" varsayımı gerçek dosyalarda tutmuyor:
 * standart Stok Giriş dosyasında başlıklar 2. satırda, 1. satır boş.
 * Bilinen sütun adlarıyla en çok eşleşen satır başlık kabul edilir; yanlış
 * pozitifi önlemek için en az 2 eşleşme aranır.
 */
function findHeaderRowIndex(aoa, maxScan = 25) {
  const wanted = new Set(Object.values(FIXED_COLUMN_LABELS).map(normalizeHeaderKey));
  let bestIdx = -1;
  let bestHits = 0;

  const limit = Math.min(aoa.length, maxScan);
  for (let i = 0; i < limit; i += 1) {
    const cells = (aoa[i] || []).map(normalizeHeaderKey).filter(Boolean);
    const hits = cells.filter((c) => wanted.has(c)).length;
    if (hits > bestHits) {
      bestHits = hits;
      bestIdx = i;
    }
  }

  return bestHits >= 2 ? bestIdx : -1;
}

/** Başlık satırı içeren ilk sayfayı seçer; sayfa sırası dosyadan dosyaya değişiyor. */
function pickSheet(wb) {
  for (const name of wb.SheetNames || []) {
    const ws = wb.Sheets[name];
    if (!ws) continue;
    const aoa = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
    const headerIdx = findHeaderRowIndex(aoa);
    if (headerIdx >= 0) return { name, aoa, headerIdx };
  }
  return null;
}

function deriveTypeFromCode(code) {
  const segs = String(code || "").trim().split(".");
  if (segs.length < 2 || segs[0] !== "150") return null;
  switch (segs[1]) {
    case "01": return "paper";
    case "02": return "ink";
    case "03": return "lak";
    case "04":
    case "07": return "shrink";
    case "05": return "label";
    case "06": return "box";
    default: return null;
  }
}

function isLotRequired(code) {
  const segs = String(code || "").trim().split(".");
  return deriveTypeFromCode(code) === "ink" ||
    (deriveTypeFromCode(code) === "paper" && segs[2] === "99");
}

function validateRow(r) {
  const errors = [];
  const code = toStr(r.material_code);
  if (isLotRequired(code) && !toStr(r.lot_no)) errors.push("Lot No zorunlu");
  if (!code) errors.push("Malzeme Kodu boş");
  const type = deriveTypeFromCode(code);
  if (code && !type) errors.push("Tip çözülemedi (150.xx.yy)");
  if (!Number.isFinite(Number(r.qty)) || Number(r.qty) <= 0) errors.push("Miktar geçersiz");
  if (type === "label") {
    if (!toStr(r.customer_name)) errors.push("Müşteri boş");
    if (!toStr(r.order_no)) errors.push("Sipariş No boş");
  }
  const pu = toStr(r.price_unit).toLowerCase();
  if (pu && !["kg", "adet", "m"].includes(pu)) errors.push("Fiyatın Birimi kg/adet/m olmalı");
  return errors;
}

function rowFingerprint(row) {
  const dimensions = Object.entries(row?.variant_dimensions || {})
    .sort(([a], [b]) => a.localeCompare(b, "tr"))
    .map(([key, value]) => [key, value]);
  return JSON.stringify([
    row?.material_code || "",
    row?.customer_name || "",
    row?.order_no || "",
    row?.line_no ?? null,
    row?.supplier_name || "",
    row?.inbound_doc_no || "",
    row?.received_by_name || "",
    row?.box_code || "",
    row?.qty ?? null,
    row?.uom || "",
    row?.qty_kg ?? null,
    row?.uom2 || "",
    row?.lot_no || "",
    row?.unit_cost ?? null,
    row?.price_unit || "",
    row?.currency || "",
    dimensions,
  ]);
}

export default function InventoryImportModal({ onClose, onImported }) {
  const [fileName, setFileName] = useState("");
  const [parseError, setParseError] = useState("");
  const [rows, setRows] = useState([]);

  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [result, setResult] = useState(null);

  const validated = useMemo(() => {
    const withErrors = (rows || []).map((r) => ({ ...r, _errors: validateRow(r) }));
    const errorCount = withErrors.reduce((acc, r) => acc + (r._errors?.length || 0), 0);
    const hasBlocking = withErrors.some((r) => (r._errors?.length || 0) > 0);
    return { rows: withErrors, errorCount, hasBlocking };
  }, [rows]);

  const duplicateRows = useMemo(() => {
    const counts = new Map();
    for (const row of rows) {
      const key = rowFingerprint(row);
      counts.set(key, (counts.get(key) || 0) + 1);
    }
    return Array.from(counts.values()).filter((count) => count > 1);
  }, [rows]);

  async function handlePickFile(file) {
    setParseError("");
    setSubmitError("");
    setResult(null);
    setRows([]);
    setFileName(file?.name || "");
    if (!file) return;

    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array", cellDates: true });
      if (!wb.SheetNames?.length) throw new Error("Excel sayfası bulunamadı");

      const picked = pickSheet(wb);
      if (!picked) {
        const firstAoa = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], {
          header: 1,
          defval: "",
        });
        const ornek = (firstAoa[0] || []).map((h) => toStr(h)).filter(Boolean);
        throw new Error(
          `Başlık satırı bulunamadı. "${FIXED_COLUMN_LABELS.material_code}" ve "${FIXED_COLUMN_LABELS.qty}" ` +
            `sütunlarını içeren bir satır olmalı. İlk sayfanın ilk satırı: ${ornek.length ? ornek.join(" | ") : "(boş)"}`
        );
      }

      const { aoa, headerIdx } = picked;
      const headerRow = aoa[headerIdx] || [];
      const headers = headerRow.map((h) => normalizeHeaderKey(h));

      const findIdx = (label) => headers.indexOf(normalizeHeaderKey(label));

      const fixedIdx = {};
      for (const [key, label] of Object.entries(FIXED_COLUMN_LABELS)) {
        fixedIdx[key] = findIdx(label);
      }

      const dimensionIdx = {};
      for (const label of DIMENSION_COLUMN_LABELS) {
        const i = findIdx(label);
        if (i >= 0) dimensionIdx[label] = i;
      }

      const parsed = [];
      for (let i = headerIdx + 1; i < aoa.length; i += 1) {
        const r = aoa[i] || [];
        const code = fixedIdx.material_code >= 0 ? toStr(r[fixedIdx.material_code]) : "";
        if (!code) continue;

        const dims = {};
        for (const [label, idx] of Object.entries(dimensionIdx)) {
          const raw = r[idx];
          if (raw === null || raw === undefined) continue;
          const v = toStr(raw);
          if (v) dims[label] = v;
        }

        const row = {
          material_code: code,
          material_name: fixedIdx.material_name >= 0 ? toStr(r[fixedIdx.material_name]) : "",
          customer_name: fixedIdx.customer_name >= 0 ? toStr(r[fixedIdx.customer_name]) : "",
          order_no: fixedIdx.order_no >= 0 ? toStr(r[fixedIdx.order_no]) : "",
          line_no: fixedIdx.line_no >= 0 ? toInt(r[fixedIdx.line_no]) : null,
          supplier_name: fixedIdx.supplier_name >= 0 ? toStr(r[fixedIdx.supplier_name]) : "",
          inbound_doc_no: fixedIdx.inbound_doc_no >= 0 ? toStr(r[fixedIdx.inbound_doc_no]) : "",
          received_by_name: fixedIdx.received_by_name >= 0 ? toStr(r[fixedIdx.received_by_name]) : "",
          box_code: fixedIdx.box_code >= 0 ? toStr(r[fixedIdx.box_code]) : "",
          qty: fixedIdx.qty >= 0 ? toNumber(r[fixedIdx.qty]) : null,
          uom: fixedIdx.uom >= 0 ? toStr(r[fixedIdx.uom]) : "",
          qty_kg: fixedIdx.qty_kg >= 0 ? toNumber(r[fixedIdx.qty_kg]) : null,
          uom2: fixedIdx.uom2 >= 0 ? toStr(r[fixedIdx.uom2]) : "",
          lot_no: fixedIdx.lot_no >= 0 ? toStr(r[fixedIdx.lot_no]) : "",
          unit_cost: fixedIdx.unit_cost >= 0 ? toNumber(r[fixedIdx.unit_cost]) : null,
          price_unit: fixedIdx.price_unit >= 0 ? toStr(r[fixedIdx.price_unit]) : "",
          currency: fixedIdx.currency >= 0 ? toStr(r[fixedIdx.currency]) : "",
          variant_dimensions: dims,
        };
        parsed.push(row);
      }

      setRows(parsed);

      // Eksik başlık ile boş dosyayı ayırt et: "Malzeme Kodu" sütunu bulunamazsa
      // her satır atlanır ve eskiden bu da "satır bulunamadı" olarak görünüp
      // kullanıcıyı yanlış yere yönlendiriyordu.
      const missingRequired = REQUIRED_FIXED_COLUMNS.filter((k) => fixedIdx[k] < 0).map(
        (k) => FIXED_COLUMN_LABELS[k]
      );
      const foundHeaders = headerRow.map((h) => toStr(h)).filter(Boolean);

      if (missingRequired.length) {
        setParseError(
          `Eksik sütun(lar): ${missingRequired.join(", ")}. ` +
            `Dosyanın ilk satırındaki başlıklar: ${foundHeaders.length ? foundHeaders.join(" | ") : "(boş)"}. ` +
            `Başlıklar dosyanın ilk satırında olmalı.`
        );
      } else if (!parsed.length) {
        setParseError(
          `Dosyada satır bulunamadı (başlık hariç). "${FIXED_COLUMN_LABELS.material_code}" sütunu dolu en az bir satır olmalı.`
        );
      }
    } catch (err) {
      console.error("EXCEL PARSE ERROR", err);
      setParseError(err?.message || "Excel okunamadı");
    }
  }

  async function submit() {
    setSubmitError("");
    setResult(null);

    if (!rows.length) return;
    if (validated.hasBlocking) {
      setSubmitError("Hatalı satırlar var. Lütfen düzeltin.");
      return;
    }

    setSaving(true);
    try {
      const payload = rows.map((r) => ({
        material_code: r.material_code,
        customer_name: r.customer_name || null,
        order_no: r.order_no || null,
        line_no: Number.isFinite(Number(r.line_no)) ? Number(r.line_no) : null,
        supplier_name: r.supplier_name || null,
        inbound_doc_no: r.inbound_doc_no || null,
        received_by_name: r.received_by_name || null,
        box_code: r.box_code || null,
        qty: Number(r.qty),
        qty_kg: r.qty_kg !== null ? Number(r.qty_kg) : null,
        lot_no: r.lot_no || null,
        unit_cost: r.unit_cost !== null ? Number(r.unit_cost) : null,
        price_unit: r.price_unit ? String(r.price_unit).toLowerCase() : null,
        currency: r.currency ? String(r.currency).toUpperCase() : "TRY",
        variant_dimensions: r.variant_dimensions || {},
      }));

      const res = await importInventory(payload);
      setResult(res);
      await onImported?.(res);
    } catch (err) {
      console.error("INVENTORY IMPORT ERROR", err);
      const status = err?.response?.status;
      const message = err?.response?.data?.error?.message || err?.message || null;
      setSubmitError(`İçe aktarılamadı${status ? ` (HTTP ${status})` : ""}${message ? `: ${message}` : ""}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onClose={saving ? undefined : onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 800 }}>
          Stok Girişi — Excel İçe Aktar
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Dosyayı seçin; malzeme tipi koddan çözülür, lot no Excel dosyasından alınır.
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          {parseError ? <Alert severity="error">{parseError}</Alert> : null}
          {submitError ? <Alert severity="error">{submitError}</Alert> : null}
          {duplicateRows.length ? (
            <Alert severity="warning">
              Dosyada aynı bilgilerle tekrarlanan {duplicateRows.length} farklı satır grubu var. Bu satırlar ayrı fiziksel
              partiler olabileceği için silinmedi ve ayrı lot olarak işlenecek. Aynı irsaliye daha önce aktarılmışsa sistem
              ikinci aktarımı engeller.
            </Alert>
          ) : null}

          {result ? (
            <Alert severity={result?.failed ? "warning" : "success"}>
              Oluşturulan kayıt: <b>{result?.created || 0}</b>, başarısız: <b>{result?.failed || 0}</b>
              {Array.isArray(result?.failures) && result.failures.length ? (
                <ul style={{ margin: "4px 0 0 16px" }}>
                  {result.failures.map((f, i) => (
                    <li key={i}>
                      <b>Satır {f.row_index + 1}{f.material_code ? ` (${f.material_code})` : ""}</b>: {f.message}
                      {Array.isArray(f.details?.warnings) && f.details.warnings.length ? (
                        <ul style={{ margin: "2px 0 0 16px" }}>
                          {f.details.warnings.map((w, j) => (
                            <li key={j}>{w}</li>
                          ))}
                        </ul>
                      ) : null}
                    </li>
                  ))}
                </ul>
              ) : null}
            </Alert>
          ) : null}

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
            <Button variant="outlined" component="label" disabled={saving}>
              Dosya Seç (.xlsx)
              <input
                type="file"
                hidden
                accept=".xlsx,.xls,.csv"
                onChange={(e) => handlePickFile(e.target.files?.[0] || null)}
              />
            </Button>
            <Typography variant="body2" color="text.secondary">
              {fileName ? `Seçili: ${fileName}` : "Henüz dosya seçilmedi"}
            </Typography>
          </Stack>

          {validated.rows.length ? (
            <Box sx={{ overflowX: "auto" }}>
              <Typography variant="body2" color={validated.hasBlocking ? "error" : "text.secondary"}>
                Satır: <b>{validated.rows.length}</b> — Hata: <b>{validated.errorCount}</b>
              </Typography>
              <Table size="small" sx={{ mt: 1 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Kod</TableCell>
                    <TableCell>Tip</TableCell>
                    <TableCell>Ad</TableCell>
                    <TableCell>Müşteri/Sipariş</TableCell>
                    <TableCell>Tedarikçi</TableCell>
                    <TableCell>Miktar</TableCell>
                    <TableCell>Varyant</TableCell>
                    <TableCell>Hata</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {validated.rows.map((r, idx) => {
                    const type = deriveTypeFromCode(r.material_code);
                    return (
                      <TableRow key={idx} hover>
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell>{r.material_code || ""}</TableCell>
                        <TableCell>{type || "?"}</TableCell>
                        <TableCell>{r.material_name || ""}</TableCell>
                        <TableCell>
                          {r.customer_name || r.order_no
                            ? `${r.customer_name || ""}${r.order_no ? ` / ${r.order_no}` : ""}${r.line_no ? ` #${r.line_no}` : ""}`
                            : ""}
                        </TableCell>
                        <TableCell>{r.supplier_name || ""}</TableCell>
                        <TableCell>{r.qty ?? ""}{r.uom ? ` ${r.uom}` : ""}</TableCell>
                        <TableCell sx={{ maxWidth: 240, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {Object.entries(r.variant_dimensions || {})
                            .map(([k, v]) => `${k}: ${v}`)
                            .join(" | ")}
                        </TableCell>
                        <TableCell>
                          {r._errors?.length ? (
                            <Typography variant="caption" color="error">{r._errors.join(", ")}</Typography>
                          ) : (
                            <Typography variant="caption" color="success.main">OK</Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          ) : null}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={saving}>
          Kapat
        </Button>
        <Button onClick={submit} variant="contained" disabled={saving || !rows.length || validated.hasBlocking}>
          {saving ? "Oluşturuluyor..." : "Stok Girişi Oluştur"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
